import { useEffect, useState, type RefObject } from "react";

/**
 * Label naast een icoon in de ingeklapte rail, bij hover én bij toetsenbordfocus. Vast gepositioneerd,
 * zodat het niet wordt afgeknipt door het scrollvak van de rail. Voor schermlezers staat het label al
 * (verborgen) in het item zelf.
 */
export function RailTooltip({ railRef }: { railRef: RefObject<HTMLElement | null> }) {
  const [tip, setTip] = useState<{ text: string; top: number; left: number } | null>(null);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const show = (e: Event) => {
      const el = (e.target as Element | null)?.closest<HTMLElement>("[data-tip]");
      if (!el || !rail.contains(el)) {
        if (e.type === "pointerover") setTip(null);
        return;
      }
      if (e.type === "focusin" && !el.matches(":focus-visible")) return;
      const r = el.getBoundingClientRect();
      setTip({ text: el.dataset.tip!, top: r.top + r.height / 2, left: rail.getBoundingClientRect().right + 6 });
    };
    const hide = () => setTip(null);
    rail.addEventListener("pointerover", show);
    rail.addEventListener("pointerleave", hide);
    rail.addEventListener("focusin", show);
    rail.addEventListener("focusout", hide);
    rail.addEventListener("scroll", hide);
    return () => {
      rail.removeEventListener("pointerover", show);
      rail.removeEventListener("pointerleave", hide);
      rail.removeEventListener("focusin", show);
      rail.removeEventListener("focusout", hide);
      rail.removeEventListener("scroll", hide);
    };
  }, [railRef]);

  if (!tip) return null;
  return (
    <span className="railtip" aria-hidden="true" style={{ top: tip.top, left: tip.left }}>
      {tip.text}
    </span>
  );
}
